import React from "react";
import {Image, StyleSheet, Text, View} from "@react-pdf/renderer";
import logoTpsWithAddress from "../statics/logos/logoTpsWithAddress.jpg";
import i18n from "../i18n/i18n";
import {Shipper} from "../transportation/models/shipper";
import {RelationType} from "../transportation/models/relation";
import {Utils} from "./utils";
import {CargoType} from "../transportation/models/cargo";
import {PaymentMethod} from "../transportation/models/transport";
import {DateUtils} from "./dateUtils";
import {OrderStatusEnum} from "../transportation/models/order";
import TransportUtils from "./transportUtils";
import {OrderUtils} from "./orderUtils";

export interface TransportNumber {
    trailerRegNo?: string;
    lorryRegNo?: string;
    wagonRegNo?: string;
    containerNo?: string;
}

export class OrderFilesUtils {
    public static styles = StyleSheet.create({
        page: {
            paddingTop: 30,
            paddingLeft: 20,
            paddingRight: 20,
            flexDirection: "row",
            backgroundColor: "#FFF",
            fontFamily: "OpenSans"
        },
        viewer: {
            width: window.innerWidth,
            height: window.innerHeight,
        },
        table: {
            borderStyle: "solid",
            borderColor: "#000",
            borderWidth: 1,
            borderRightWidth: 0,
            borderBottomWidth: 0
        },
        tableRow: {
            margin: "auto",
            flexDirection: "row"
        },
        tableCol: {
            minHeight: 50,
            width: "50%",
            borderStyle: "solid",
            borderColor: "#000",
            borderWidth: 1,
            borderLeftWidth: 0,
            borderTopWidth: 0
        },
        tableSmallCol: {
            minHeight: 50,
            width: "25%",
            borderStyle: "solid",
            borderColor: "#000",
            borderWidth: 1,
            borderLeftWidth: 0,
            borderTopWidth: 0
        },
        tableMediumCol: {
            minHeight: 50,
            width: "75%",
            borderStyle: "solid",
            borderColor: "#000",
            borderWidth: 1,
            borderLeftWidth: 0,
            borderTopWidth: 0
        },
        tableBigCol: {
            minHeight: 50,
            width: "100%",
            borderStyle: "solid",
            borderColor: "#000",
            borderWidth: 1,
            borderLeftWidth: 0,
            borderTopWidth: 0
        },
        tableCellHeader: {
            marginLeft: 5,
            marginRight: 5,
            marginTop: 2,
            marginBottom: 5,
            fontSize: 8
        },
        tableCellData: {
            marginLeft: 5,
            marginRight: 5,
            marginBottom: 5,
            fontSize: 10,
            fontFamily: "OpenSansSemiBold",
            textAlign: "center"
        }
    });

    public static equipmentAndDamageStyles = StyleSheet.create({
        tableHeaderColumn: {
            minHeight: 35,
            borderBottom: 2
        },
        tableHeaderText: {
            fontFamily: "OpenSansBold",
            marginTop: "auto",
            marginBottom: "auto",
            marginLeft: 5
        },
        tableCellText: {
            fontSize: 9
        },
        textCenterInCell: {
            marginTop: "auto",
            marginBottom: "auto",
            textAlign: "center"
        }
    });

    public static tcrReportStyles = StyleSheet.create({
        header: {
            marginLeft: 5,
            fontSize: 8
        },
        smallSquare: {
            width: 13,
            height: 13,
            border: "1px solid #000"
        },
        bigSquare: {
            width: 140,
            height: 140,
            border: "1px solid #000",
            marginRight: 10
        },
        row: {
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: 20,
            marginLeft: 5,
            marginRight: 5
        },
        column: {
            height: "100%",
            flexDirection: "row",
            alignItems: "center"
        },
        checked: {
            fontSize: 9,
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center"
        },
        trailerPhotoRow: {
            display: "flex",
            flexDirection: "row",
            marginBottom: 5
        },
        centerPicture: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "auto",
            marginBottom: "auto"
        },
        pictureSize: {
            width: 125,
            height: 125
        }
    });

    public static renderHeader(title: string): JSX.Element {
        const styles = StyleSheet.create({
            header: {
                width: "100%",
                flexDirection: "column",
                alignItems: "center"
            },
            headerImage: {
                height: 55
            },
            title: {
                marginTop: 10,
                marginRight: "auto",
                marginBottom: 15,
                marginLeft: "auto",
                fontSize: 13,
                weight: 700,
                textAlign: "center",
                fontFamily: "OpenSansBold"
            }
        });

        return (
            <View style={styles.header} fixed>
                <Image style={styles.headerImage} src={logoTpsWithAddress}/>
                <Text style={styles.title}>
                    {title ?? ""}
                </Text>
            </View>
        );
    }

    public static renderTransportType(relationType: RelationType): string {
        switch (relationType) {
            case RelationType.ROAD_DEPO_FERRY:
            case RelationType.FERRY_DEPO_ROAD:
                return i18n.t("transportation.order.details.files.transportType.road");
            case RelationType.RAIL_DEPO_FERRY:
            case RelationType.FERRY_DEPO_RAIL:
                return i18n.t("transportation.order.details.files.transportType.rail");
            default:
                return "";
        }
    }

    public static renderMeansOfTransportNo(transportNo: TransportNumber): string {
        const result = [];

        result.push(transportNo.trailerRegNo);
        result.push(transportNo.lorryRegNo);
        result.push(transportNo.wagonRegNo);
        result.push(transportNo.containerNo);

        return result.filter(n => n).join(" / ");
    }

    public static renderOrderType(relationType: RelationType) {
        return <View style={this.styles.tableSmallCol}>
            <Text style={this.styles.tableCellHeader}>
                Rodzaj zlecenia /{"\n"}
                Type of order
            </Text>
            <Text style={this.styles.tableCellData}>
                {i18n.t(`relationType.${relationType}`)}
            </Text>
        </View>;
    }

    public static renderOrderNo(orderId: string) {
        return <View style={this.styles.tableSmallCol}>
            <Text style={this.styles.tableCellHeader}>
                Nr zlecenia /{"\n"}
                Order no
            </Text>
            <Text style={this.styles.tableCellData}>
                {Utils.wrapText(orderId, 20, "")}
            </Text>
        </View>;
    }

    public static renderBookingNo(bookingNo: string) {
        return <View style={this.styles.tableSmallCol}>
            <Text style={this.styles.tableCellHeader}>
                Nr rezerwacji /{"\n"}
                Booking no
            </Text>
            <Text style={this.styles.tableCellData}>
                {bookingNo}
            </Text>
        </View>;
    }

    public static renderVoyageNo(voyageNo: string | null | undefined) {
        return <View style={this.styles.tableSmallCol}>
            <Text style={this.styles.tableCellHeader}>
                Nr podróży /{"\n"}
                Voyage no
            </Text>
            <Text style={this.styles.tableCellData}>
                {voyageNo || ""}
            </Text>
        </View>;
    }

    public static renderCargoType(cargoType: CargoType) {
        return <View style={this.styles.tableSmallCol}>
            <Text style={this.styles.tableCellHeader}>
                Rodzaj ładunku /{"\n"}
                Cargo type
            </Text>
            <Text style={this.styles.tableCellData}>
                {i18n.t(`cargoType.${cargoType}`)}
            </Text>
        </View>;
    }

    public static renderCbm(cbm: number) {
        return <View style={this.styles.tableSmallCol}>
            <Text style={this.styles.tableCellHeader}>
                CBM
            </Text>
            <Text style={this.styles.tableCellData}>
                {cbm}
            </Text>
        </View>;
    }

    public static renderDangerousGoods(isDangerousGoods: boolean) {
        return <>
            <Text style={this.styles.tableCellHeader}>
                Ładunek niebezpieczny /{"\n"}
                Dangerous goods
            </Text>
            <Text style={this.styles.tableCellData}>
                {Utils.renderBooleanYesNoValue(isDangerousGoods, false)}
            </Text>
        </>;
    }

    public static requestedTemp(requestedTemp: number, requestedTempNumber: number) {
        return <>
            <Text style={this.styles.tableCellHeader}>
                Żądana temperatura {requestedTempNumber} /{"\n"}
                Requested temperature {requestedTempNumber}
            </Text>
            <Text style={this.styles.tableCellData}>
                {Utils.renderValueWithUnit(requestedTemp, "°C")}
            </Text>
        </>;
    }

    public static requestedSignatures(isImport: boolean, username: string) {
        const carrier = isImport ?
            <>
                Przewoźnik\Odbiorca /{"\n"}
                Carrier\Recipient
            </> :
            <>
                Przewoźnik\Składający /{"\n"}
                Carrier\Submitter
            </>;

        return <>
            <View style={this.styles.tableRow}>
                <View style={[this.styles.tableBigCol, {minHeight: 20}]}>
                    <Text style={this.styles.tableCellHeader}>
                        Podpisy / Signatures
                    </Text>
                </View>
            </View>
            <View style={this.styles.tableRow}>
                <View style={this.styles.tableCol}>
                    <Text style={this.styles.tableCellHeader}>
                        {carrier}
                    </Text>
                </View>
                <View style={this.styles.tableCol}>
                    {this.renderPortServiceOfTPS(username)}
                </View>
            </View>
        </>;
    }

    public static requestedSignaturesForDepo(username: string, isToDepoStatus: boolean) {
        return <>
            <View style={this.styles.tableRow}>
                <View style={[this.styles.tableBigCol, {minHeight: 20}]}>
                    <Text style={this.styles.tableCellHeader}>
                        Podpisy / Signatures
                    </Text>
                </View>
            </View>
            <View style={this.styles.tableRow}>
                <View style={this.styles.tableSmallCol}>
                    <Text style={this.styles.tableCellHeader}>
                        Przewoźnik\Składający /{"\n"}
                        Carrier\Submitter
                    </Text>
                </View>
                <View style={this.styles.tableSmallCol}>
                    {this.renderPortServiceOfTPS(isToDepoStatus ? username : "")}
                </View>
                <View style={this.styles.tableSmallCol}>
                    {this.renderPortServiceOfTPS(!isToDepoStatus ? username : "")}
                </View>
                <View style={this.styles.tableSmallCol}>
                    <Text style={this.styles.tableCellHeader}>
                        Przewoźnik\Odbiorca /{"\n"}
                        Carrier\Recipient
                    </Text>
                </View>
            </View>
        </>;
    }

    public static renderPortServiceOfTPS(username: string) {
        return <>
            <Text style={this.styles.tableCellHeader}>
                Serwis Portowy TPŚ /{"\n"}
                Port Service of TPŚ
            </Text>
            <Text style={[this.styles.tableCellData, {textAlign: "left"}]}>
                {username}
            </Text>
        </>;
    }

    public static renderPaymentMethod(paymentMethod: PaymentMethod) {
        const paymentMethodValue = paymentMethod ? i18n.t(`paymentMethod.${paymentMethod}`) : "";

        return <View style={this.styles.tableSmallCol}>
            <Text style={this.styles.tableCellHeader}>
                Forma płatności /{"\n"}
                Payment method
            </Text>
            <Text style={this.styles.tableCellData}>
                {paymentMethodValue}
            </Text>
        </View>;
    }

    public static renderPaymentDocumentNumber(paymentDocumentNumber: string) {
        return <View style={this.styles.tableMediumCol}>
            <Text style={this.styles.tableCellHeader}>
                Numer dokumentu płatności /{"\n"}
                Payment document number
            </Text>
            <Text style={this.styles.tableCellData}>
                {paymentDocumentNumber}
            </Text>
        </View>;
    }

    public static renderRemarks(remarks?: string) {
        return <View style={this.styles.tableRow}>
            <View style={this.styles.tableBigCol}>
                <Text style={this.styles.tableCellHeader}>
                    Uwagi /{"\n"}
                    Remarks
                </Text>
                <Text style={[this.styles.tableCellData, {textAlign: "left"}]}>
                    {remarks}
                </Text>
            </View>
        </View>;
    }

    public static renderCargoWeight(weight: number) {
        return <>
            <Text style={[this.styles.tableCellHeader]}>
                Waga ładunku /{"\n"}
                Cargo weight
            </Text>
            <Text style={this.styles.tableCellData}>
                {Utils.renderValueWithUnit(weight, "kg")}
            </Text>
        </>;
    }

    public static renderArrivalDepartureTime(arrivalDepartureTime: Date | null) {
        return <>
            <Text style={[this.styles.tableCellHeader]}>
                Planowany czas złożenia/godz. /{"\n"}
                Planned time of submission/hour
            </Text>
            <Text style={this.styles.tableCellData}>
                {DateUtils.getFormattedDateWithTime(arrivalDepartureTime)}
            </Text>
        </>;
    }

    public static renderTransportCompany(transportCompany: Shipper | string | null) {
        if (transportCompany === null) {
            return <>
                <Text style={this.styles.tableCellHeader}>
                    Firma Transportowa: Nazwa firmy i adres /{"\n"}
                    Transport company: Company name and address
                </Text>
                <Text style={this.styles.tableCellData}>
                </Text>
            </>;
        } else if (typeof transportCompany === "string") {
            return <>
                <Text style={this.styles.tableCellHeader}>
                    Firma Transportowa: Nazwa firmy i adres /{"\n"}
                    Transport company: Company name and address
                </Text>
                <Text style={this.styles.tableCellData}>
                    {transportCompany}
                </Text>
            </>;
        } else {
            const result = [transportCompany.name, transportCompany.address, transportCompany.postalCode, transportCompany.city, transportCompany.country];

            return <>
                <Text style={this.styles.tableCellHeader}>
                    Firma Transportowa: Nazwa firmy i adres /{"\n"}
                    Transport company: Company name and address
                </Text>
                <Text style={this.styles.tableCellData}>
                    {result.filter(n => n).join(", ")}
                </Text>
            </>;
        }
    }

    public static renderFerry(ferryName: string) {
        return <>
            <Text style={[this.styles.tableCellHeader]}>
                Prom /{"\n"}
                Ferry
            </Text>
            <Text style={this.styles.tableCellData}>
                {ferryName}
            </Text>
        </>;
    }

    public static renderTimeOfDepositingLoad(timeOfDepositingLoad: Date | null) {
        return <View style={this.styles.tableSmallCol}>
            <Text style={this.styles.tableCellHeader}>
                Czas złożenia ładunku /{"\n"}
                Time of depositing the load
            </Text>
            <Text style={this.styles.tableCellData}>
                {DateUtils.getFormattedDateWithTime(timeOfDepositingLoad)}
            </Text>
        </View>;
    }

    public static renderStorageTime(storageTime: number) {
        return <>
            <Text style={this.styles.tableCellHeader}>
                Czas składowania/H /{"\n"}
                Storage time/H
            </Text>
            <Text style={this.styles.tableCellData}>
                {storageTime ? Math.ceil(storageTime/60) : null}
            </Text>
        </>;
    }

    public static renderPaymentWaiver(parkingCost: string | undefined) {
        return <View style={this.styles.tableSmallCol}>
            <Text style={this.styles.tableCellHeader}>
                Zwolnienie z opłaty /{"\n"}
                Payment waiver
            </Text>
            <Text style={this.styles.tableCellData}>
                {OrderUtils.calculatePaymentWaiver(parkingCost)}
            </Text>
        </View>;
    }

    public static renderPinCode(pinCode: string) {
        return <View style={this.styles.tableSmallCol}>
            <Text style={this.styles.tableCellHeader}>
                Pin /{"\n"}
                Pin code
            </Text>
            <Text style={this.styles.tableCellData}>
                {pinCode}
            </Text>
        </View>;
    }

    public static renderFreeTimeFromFees(freeTimeFromFees: number) {
        return <View style={this.styles.tableSmallCol}>
            <Text style={this.styles.tableCellHeader}>
                Czas wolny od opłat do /{"\n"}
                Free time from fees until
            </Text>
            <Text style={this.styles.tableCellData}>
                {TransportUtils.generateTimeInHoursWithMinutesFormat(freeTimeFromFees, false)}
            </Text>
        </View>;
    }

    public static isExportRelationType(relationType: RelationType | undefined | ""): boolean {
        if (!relationType) return false;

        return (
            relationType === RelationType.ROAD_DEPO_FERRY ||
            relationType === RelationType.RAIL_DEPO_FERRY
        );
    }

    public static isImportRelationType(relationType: RelationType | undefined | ""): boolean {
        if (!relationType) return false;

        return (
            relationType === RelationType.FERRY_DEPO_ROAD ||
            relationType === RelationType.FERRY_DEPO_RAIL
        );
    }

    public static getOrderPrintButtonType(cargoType: CargoType | undefined | ""): "none" | "cargo" | "trailerOrContainer" {
        if (!cargoType) return "none";

        switch (cargoType) {
            case CargoType.NORMATIVE_GENERAL_CARGO:
            case CargoType.DANGEROUS_GENERAL_CARGO:
            case CargoType.OVERSIZE:
                return "cargo";
            case CargoType.NORMATIVE_TRAILER:
            case CargoType.NORMATIVE_CONTAINER:
            case CargoType.DANGEROUS_CONTAINER:
            case CargoType.DANGEROUS_TRAILER:
            case CargoType.OVERSIZE_TRAILER:
                return "trailerOrContainer";
            default:
                return "none";
        }
    }

    public static renderTypeOfMeansOfTransportSection(relationType: RelationType, isToDepo: boolean, lorryRegNo: string | undefined, wagonRegNo: string | undefined) {
        return (
            <>
                <View style={[this.styles.tableSmallCol]}>
                    <Text style={[this.styles.tableCellHeader]}>
                        Rodzaj środka transportu /{"\n"}
                        Type of means of transport
                    </Text>
                    <Text style={this.styles.tableCellData}>
                        {OrderFilesUtils.renderTransportTypeForNoSeaTrafficRelationType(relationType, isToDepo)}
                    </Text>
                </View>
                <View style={[this.styles.tableSmallCol]}>
                    <Text style={[this.styles.tableCellHeader]}>
                        Nr identyfikacyjny /{"\n"}
                        Identification no
                    </Text>
                    <Text style={this.styles.tableCellData}>
                        {OrderFilesUtils.renderMeansOfTransportNo({
                            lorryRegNo: lorryRegNo,
                            wagonRegNo: wagonRegNo
                        })}
                    </Text>
                </View>
            </>
        );
    }

    public static renderTransportTypeForNoSeaTrafficRelationType(relationType: RelationType, isToDepo: boolean): string {
        if (isToDepo) {
            switch (relationType) {
                case RelationType.ROAD_DEPO_ROAD:
                case RelationType.ROAD_DEPO_RAIL:
                case RelationType.ROAD_DEPO:
                    return i18n.t("transportation.order.details.files.transportType.road");
                case RelationType.RAIL_DEPO_ROAD:
                case RelationType.RAIL_DEPO_RAIL:
                case RelationType.RAIL_DEPO:
                    return i18n.t("transportation.order.details.files.transportType.rail");
                default:
                    return "";
            }
        } else {
            switch (relationType) {
                case RelationType.ROAD_DEPO_ROAD:
                case RelationType.RAIL_DEPO_ROAD:
                    return i18n.t("transportation.order.details.files.transportType.road");
                case RelationType.ROAD_DEPO_RAIL:
                case RelationType.RAIL_DEPO_RAIL:
                    return i18n.t("transportation.order.details.files.transportType.rail");
                default:
                    return "";
            }
        }
    }

    public static isToDepoStatus(status: OrderStatusEnum | ""): boolean {
        switch(status) {
            case OrderStatusEnum.REGISTERED:
            case OrderStatusEnum.PARKING_ENTERED:
            case OrderStatusEnum.PARKING_EXITED:
            case OrderStatusEnum.DEPO_ENTERED:
                return true;
            case OrderStatusEnum.DEPO_EXITED:
            case OrderStatusEnum.FERRY_ENTERED:
            case OrderStatusEnum.FERRY_EXITED:
                return false;
            default:
                return false;
        }
    }
}